'use client';

import Flag from '@/components/atoms/Flag';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import { SmartLink } from '@/components/SmartLink';
import type { Comedian_Entry } from '@/types/custom';
import type { EventWithImages } from '@/types/query-types';
import { cn } from '@/utils/cn';
import {
    getShowPageLink,
    getShowPrimaryLanguage,
    getShowStatus,
    getShowSubtitle,
    getShowTitle,
} from '@/utils/show';
import ArtistAvatarList from './ArtistAvatarList';
import ShowTilePurchaseButton from './molecules/ShowTilePurchaseButton';
import ShowDateTime from './ShowDateTime';

interface ShowTileProps {
    event: EventWithImages;
    priority?: boolean;
    className?: string;
    locale: string;
}

export default function ShowTile({
    event,
    priority = false,
    className = '',
    locale,
}: ShowTileProps) {
    const status = getShowStatus(event);
    const language = getShowPrimaryLanguage(event);
    const title = getShowTitle(event);
    const subtitle = getShowSubtitle(event);
    const image = event.eventTileImage?.[0] ?? null;

    const artists = event.artists as Comedian_Entry[];

    return (
        <SmartLink
            href={getShowPageLink(event)}
            className={cn(
                'relative flex bg-haug-ivory-100 font-neue-haas',
                className,
                'cursor-pointer',
                'focus:outline-none focus:ring-2 focus:ring-haug-coral-100'
            )}
        >
            {/* Overlay for canceled events */}
            {status === 'cancelled' && <div className="absolute inset-0 z-10 opacity-60" />}

            <div className="event-tile__image relative w-48 min-w-48 overflow-hidden">
                <div
                    className="event-tile__image__background relative block aspect-h-3 aspect-w-4 w-full"
                    style={{ mixBlendMode: 'luminosity' }}
                >
                    {image?.urlMedium && (
                        <OptimizedImage
                            image={image}
                            sizes="(max-width: 768px) 50vw, (max-width: 1200px) 30vw, 50vw"
                            priority={priority}
                            className="object-cover"
                            fallbackAlt={title || ''}
                        />
                    )}
                </div>
            </div>

            <div
                className={cn(
                    'relative flex flex-col justify-between',
                    'flex-shrink-0 flex-grow basis-0'
                )}
            >
                {/* Event Date Time */}
                <div className={cn('event-tile__datetime', 'flex w-full flex-column flex-wrap')}>
                    <div
                        className={cn(
                            'w-auto font-bold uppercase',
                            'event-colors-text-color',
                            'text-sm',
                            'bg-haug-ivory-30',
                            'flex items-center',
                            'gap-x-3 px-3'
                        )}
                    >
                        <div className="pt-1">
                            <ShowDateTime event={event} locale={locale} />
                        </div>

                        <div className="event-tile__flag flex h-5 w-auto items-center justify-center">
                            <Flag language={language} />
                        </div>
                    </div>
                </div>

                {/* Event Info */}
                <div className="event-title__info p-2 pb-0">
                    {/* Title */}
                    <div className="event-title__title mb-2 mb-4 w-full break-words font-bold text-xl">
                        {title}
                    </div>

                    {/* Subtitle */}
                    <div className="event-title__subtitle mb-2 w-full break-words text-sm">
                        {subtitle && <p>{subtitle}</p>}
                    </div>

                    {/* Artist Avatars */}
                    {artists?.length > 0 && <ArtistAvatarList artists={artists} />}
                </div>

                {/* Purchase button placeholder */}
                <ShowTilePurchaseButton event={event} />
            </div>
        </SmartLink>
    );
}
